<script setup>
import {computed, inject, reactive, ref} from "vue";
//import functions
import {numeric, required} from "@vuelidate/validators";
import {showMessage} from "@/utils/showMessage.js";
//import components
import {InputText, FloatLabel, Dialog} from "primevue";
import {Icon} from "@iconify/vue";
//import stores
import {useUserStore} from "@/stores/user.js";
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useToast} from "primevue/usetoast";
import {useConfigData} from "@/composables/useConfigData.js";
import {useVuelidate} from "@vuelidate/core";
import Select from "primevue/select";
import Attachments from "@/views/orders/Details/Attachments.vue";
import {iranianPhone} from "@/utils/iranianPhone.js";

//stores
const userStore = useUserStore();

//loading flags
const {
  saving,
  setSaving,
} = useLoadingFlags()

//composables
const {isXL} = useBreakpoint();
const toast = useToast();
const {games} = useConfigData('Currency')

//emits
const emit = defineEmits(['refresh']);

//props
const props = defineProps({
  user: {
    type: Object,
    required: true
  }
});

//injects
const visible = inject('isShowKYCInfoModal')

//component variables
const states = ['New', 'Delivering', 'Delivered', 'Canceled', 'Paid']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}

const isUploadingIDCard = ref(false)
const isUploadingSelfie = ref(false)
const kycInfo = reactive({
  "id": props.user.id,
  "firstName": props.user.firstName ?? null,
  "lastName": props.user.lastName ?? null,
  "phoneNumber": props.user.phoneNumber ? props.user.phoneNumber : null,
  "address": props.user.address ?? null,
  "kycIdCard": props.user.kycIdCard ?? {},
  "kycSelfie": props.user.kycSelfie ?? {},
})
const rules = computed(() => {
  return {
    firstName: {required},
    lastName: {required},
    phoneNumber: {numeric, required, iranianPhone},
    address: {required},
  };
});
const $vuelidate = useVuelidate(rules, kycInfo);
const isSubmitted = ref(false)

//global functions
//component functions
const onSave = async () => {
  isSubmitted.value = true
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }

  try {
    saving.value = true
    const res = await userStore.action_saveIdentificationDocuments({
      profile: {
        id: props.user.id
      },
      ...kycInfo
    })
    if (res.result === true) {
      showMessage('KYC Info edited.', 'success');
      emit('refresh')
      setTimeout(() => {
        visible.value = false
      }, 200)
      isSubmitted.value = false
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

const setKYCIdCard = (val) => {
  if (val.length > 0) {
    kycInfo.kycIdCard = val[0]
  }
}

const setKYCSelfie = (val) => {
  if (val.length > 0) {
    kycInfo.kycSelfie = val[0]
  }
}

//hooks

//provides
</script>
<template>
  <Dialog v-model:visible="visible"
          :class="['lg:min-w-max !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="flex flex-col gap-4 overflow-hidden h-full w-full lg:max-w-3xl max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Edit KYC Info
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-y-auto pt-4 pb-1 text-xs 2xl:text-sm">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="firstName" v-model="kycInfo.firstName" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vuelidate.firstName.$error }"/>
            <label for="firstName" class="!bg-slate-900"
                   :class="{ 'focus:text-red-300': $vuelidate.firstName.$error }">First Name</label>
          </FloatLabel>
          <template v-if="$vuelidate.firstName.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.firstName.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="lastName" v-model="kycInfo.lastName" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vuelidate.lastName.$error }"/>
            <label for="lastName" class="!bg-slate-900">Last Name</label>
          </FloatLabel>
          <template v-if="$vuelidate.lastName.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.lastName.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <!--        <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                  <FloatLabel variant="on" class="w-full custom-select">
                    <Select
                        v-model="kycInfo.state"
                        inputId="state"
                        :options="states"
                        class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                        :class="[$vuelidate.state.$error ? '!border !border-red-300' : '!border-slate-700']"
                        input-class="!py-1 !items-center !flex !pr-0"
                        overlay-class="select-overlay-in-modal h-max"
                    >
                      <template #value="slotProps">
                        <div class="flex items-center gap-2 !text-xs">
                          <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.value]"/>
                          {{ slotProps.value }}
                        </div>
                      </template>

                      <template #option="slotProps">
                        <div class="flex items-center gap-2 !text-xs 2xl:!text-sm">
                          <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.option]"/>
                          {{ slotProps.option }}
                        </div>
                      </template>
                    </Select>
                    <label for="">Verification State</label>
                  </FloatLabel>

                  <p v-if="$vuelidate.state.$error" class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs">
                    This field is required
                  </p>
                </div>-->
        <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText v-allow-digits id="phoneNumber" v-model="kycInfo.phoneNumber" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vuelidate.phoneNumber.$error }"/>
            <label for="phoneNumber" class="!bg-slate-900">Phone Number</label>
          </FloatLabel>
          <template v-if="$vuelidate.phoneNumber.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.phoneNumber.required.$response">
              This field is required
            </p>
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.phoneNumber.iranianPhone.$response">
              Invalid phone number
            </p>
          </template>
        </div>
        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <FloatLabel variant="on">
            <InputText id="address" v-model="kycInfo.address" autocomplete="off"
                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                       :class="{ 'has-error': $vuelidate.address.$error }"/>
            <label for="address" class="!bg-slate-900">Address</label>
          </FloatLabel>
          <template v-if="$vuelidate.address.$error">
            <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
               v-if="!$vuelidate.address.required.$response">
              This field is required
            </p>
          </template>
        </div>
        <div class="col-span-6 flex flex-col gap-1">
          <p class="text-xs text-slate-300">ID Card</p>
          <Attachments
              id="idCard"
              fullWidth
              coverImage
              sizeClass="w-full h-24"
              fontSize="text-xs"
              :count="1"
              :required="kycInfo.kycIdCard?.action === 'remove' && isSubmitted"
              :backend-images="[kycInfo.kycIdCard] ?? []"
              :only-changes="true"
              v-model:uploading="isUploadingIDCard"
              @update:payload="setKYCIdCard"
          />
        </div>
        <div class="col-span-6 flex flex-col gap-1">
          <p class="text-xs text-slate-300">Selfie</p>
          <Attachments
              id="selfie"
              fullWidth
              coverImage
              sizeClass="w-full h-24"
              fontSize="text-xs"
              :count="1"
              :required="kycInfo.kycSelfie?.action === 'remove' && isSubmitted"
              :backend-images="[kycInfo.kycSelfie] ?? []"
              :only-changes="true"
              v-model:uploading="isUploadingSelfie"
              @update:payload="setKYCSelfie"
          />
        </div>
      </div>
      <!--        <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                <FloatLabel variant="on">
                  <InputText id="lowestPricesLink" v-model="discordInfo.discordAvatar" autocomplete="off"
                             class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                  <label for="lowestPricesLink" class="!bg-slate-900">Discord Avatar</label>
                </FloatLabel>
              </div>-->
    </div>

    <div class="flex justify-end gap-3 select-none">
      <button @click="onSave"
              class="btn btn-outline-dark flex items-center justify-center gap-3 disabled:opacity-50 py-1">
        <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
        Save
      </button>
    </div>
  </Dialog>
</template>
<style></style>